/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description React Hook useBlobUpload
 */
import { useCallback, useState } from 'react';
import _ from 'lodash';
import type { UploadProps } from 'antd';
import { useQueryClient } from '@tanstack/react-query';

import QUERY_KEYS from 'app-constants/queryKeys';
import useGetBlobPresignedUrl from 'hooks/mutate/useGetBlobPresignedUrl';
import {
	BlobUploadItem,
	getAttachmentValidationMessage,
	getFileSizeLimitMB,
	getUploadAcceptConfig,
	getUploadErrorMessage,
	createBlobUploadItem,
	createCloudflareUploadFile,
	isBlobUploadInFlight,
	partitionAttachmentFiles,
	uploadFileToPresignedUrl,
} from 'utils/uploadAttachment';

type IProps = {
	blobCategory: S3BlobCategory;
	entityType?: EntityTypeEnum;
	allowedFileTypes?: string[];
	accept?: UploadProps['accept'];
	maxFileSize?: number | null;
	onFileUploaded?: (file: PatientPayment.ICloudflareUploadFile) => void;
	onError?: (message: string) => void;
};

type IUseBlobUpload = {
	uploads: BlobUploadItem[];
	uploadAccept?: string;
	activeUploadCount: number;
	isUploading: boolean;
	uploadFiles: (files: File[]) => Promise<void>;
	removeUpload: (uploadId: string) => void;
};

/**
 * useBlobUpload
 * @returns local blob upload flow state and actions
 */
const useBlobUpload = ({
	blobCategory,
	entityType,
	allowedFileTypes,
	accept,
	maxFileSize,
	onFileUploaded,
	onError,
}: IProps): IUseBlobUpload => {
	const queryClient = useQueryClient();
	const { mutateAsync: getBlobPresignedUrl } = useGetBlobPresignedUrl();
	const [uploads, setUploads] = useState<BlobUploadItem[]>([]);
	const { acceptedExtensions, accept: uploadAccept } = getUploadAcceptConfig(allowedFileTypes, accept);
	const fileSizeLimitMB = getFileSizeLimitMB(maxFileSize);

	const updateUpload = useCallback((uploadId: string, update: Partial<BlobUploadItem>) => {
		setUploads((currentUploads) => currentUploads.map((upload) => (upload.id === uploadId ? { ...upload, ...update } : upload)));
	}, []);

	const removeUpload = useCallback((uploadId: string) => {
		setUploads((currentUploads) => currentUploads.filter((upload) => upload.id !== uploadId));
	}, []);

	const uploadBlobFile = useCallback(
		async (file: File, uploadId: string): Promise<PatientPayment.ICloudflareUploadFile> => {
			const data = await getBlobPresignedUrl({
				blobCategory,
				entityType,
			});

			updateUpload(uploadId, { status: 'uploading', progress: 0, error: undefined });

			await uploadFileToPresignedUrl({
				file,
				presignedUrl: data.presignedUrl,
				onProgress: (progress) => updateUpload(uploadId, { status: 'uploading', progress }),
			});

			return createCloudflareUploadFile(file, data);
		},
		[blobCategory, entityType, getBlobPresignedUrl, updateUpload],
	);

	const uploadFiles = useCallback(
		async (files: File[]) => {
			if (files.length === 0) {
				return;
			}

			const { validFiles, errorMessages } = partitionAttachmentFiles(files, acceptedExtensions, fileSizeLimitMB);
			_.forEach(errorMessages, (errorMessage) => onError?.(getAttachmentValidationMessage(errorMessage, maxFileSize)));

			if (_.isEmpty(validFiles)) {
				return;
			}

			const nextUploads = _.map(validFiles, createBlobUploadItem);

			setUploads((currentUploads) => [...currentUploads, ...nextUploads]);

			const results = await Promise.allSettled(_.map(validFiles, (file, index) => uploadBlobFile(file, nextUploads[index].id)));

			let hasSuccessfulUpload = false;
			_.forEach(results, (result, index) => {
				const uploadId = nextUploads[index].id;
				if (result.status === 'fulfilled') {
					hasSuccessfulUpload = true;
					onFileUploaded?.(result.value);
					removeUpload(uploadId);
					return;
				}

				const errorMessage = getUploadErrorMessage(result.reason, validFiles[index].name);
				updateUpload(uploadId, { status: 'failed', error: errorMessage });
				onError?.(errorMessage);
			});

			if (hasSuccessfulUpload) {
				void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.blobData] });
			}
		},
		[
			acceptedExtensions,
			fileSizeLimitMB,
			maxFileSize,
			onError,
			onFileUploaded,
			queryClient,
			removeUpload,
			uploadBlobFile,
			updateUpload,
		],
	);

	const activeUploadCount = _.size(_.filter(uploads, isBlobUploadInFlight));

	return {
		uploads,
		uploadAccept,
		activeUploadCount,
		isUploading: activeUploadCount > 0,
		uploadFiles,
		removeUpload,
	};
};

export default useBlobUpload;
